<script setup lang="ts">
import type { TableColumn } from '@nuxt/ui'
import type { Invoice, InvoiceLineItem, InvoiceStatus } from '@/types'
import { Head, Link, router, useForm } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import { firstFormError } from '@/lib/forms'
import { invoiceStatusColor, settlementMonth, statusLabel } from '@/lib/invoices'
import { dashboardLayout } from '@/lib/layouts'
import { formatGrosz } from '@/lib/money'
import { adminInvoicePath, adminInvoicesPath } from '@/routes'

defineOptions({
  layout: dashboardLayout,
})

const props = defineProps<{
  invoice: Invoice
  lineItems: InvoiceLineItem[]
}>()

const title = computed(() => `Invoice · ${props.invoice.customer?.name ?? ''}`)

const form = useForm({
  status: props.invoice.status,
  notes: props.invoice.notes ?? '',
})

const transitionItems = computed(() =>
  (props.invoice.allowed_transitions ?? []).map(value => ({
    label: statusLabel(value),
    value,
  })),
)

function submitStatus(status: InvoiceStatus) {
  form.status = status
  form.patch(adminInvoicePath(props.invoice.id), { preserveScroll: true })
}

function submitNotes() {
  form.patch(adminInvoicePath(props.invoice.id), { preserveScroll: true })
}

const deleteOpen = ref(false)
const deleting = ref(false)

function destroy() {
  router.delete(adminInvoicePath(props.invoice.id), {
    onStart: () => (deleting.value = true),
    onFinish: () => (deleting.value = false),
  })
}

const columns: TableColumn<InvoiceLineItem>[] = [
  { accessorKey: 'product_name', header: 'Product' },
  { accessorKey: 'kind', header: 'Kind' },
  { accessorKey: 'usage_month', header: 'Usage month' },
  { accessorKey: 'quantity', header: 'Quantity' },
  { accessorKey: 'unit_price', header: 'Unit price' },
  { accessorKey: 'amount', header: 'Amount' },
  { accessorKey: 'flags', header: 'Flags' },
]
</script>

<template>
  <Head :title="title" />

  <UDashboardPanel id="invoice-show">
    <template #header>
      <UDashboardNavbar :title="title">
        <template #leading>
          <UButton
            :to="adminInvoicesPath()"
            :as="Link"
            icon="i-lucide-arrow-left"
            color="neutral"
            variant="ghost"
            square
          />
        </template>

        <template #right>
          <UBadge :color="invoiceStatusColor(invoice.status)" variant="subtle">
            {{ statusLabel(invoice.status) }}
          </UBadge>
        </template>
      </UDashboardNavbar>
    </template>

    <template #body>
      <div class="flex flex-col gap-8">
        <div class="ring-default rounded-lg p-5 ring">
          <dl class="grid gap-4 sm:grid-cols-2">
            <div>
              <dt class="text-muted text-xs uppercase tracking-wide">
                Customer
              </dt>
              <dd class="text-highlighted font-medium">
                {{ invoice.customer?.name }}
              </dd>
            </div>
            <div>
              <dt class="text-muted text-xs uppercase tracking-wide">
                Settlement month
              </dt>
              <dd class="text-highlighted">
                {{ settlementMonth(invoice.settlement_month) }}
              </dd>
            </div>
            <div>
              <dt class="text-muted text-xs uppercase tracking-wide">
                Finalized at
              </dt>
              <dd class="text-highlighted">
                {{ invoice.finalized_at ? new Date(invoice.finalized_at).toLocaleString() : '—' }}
              </dd>
            </div>
            <div>
              <dt class="text-muted text-xs uppercase tracking-wide">
                Paid at
              </dt>
              <dd class="text-highlighted">
                {{ invoice.paid_at ? new Date(invoice.paid_at).toLocaleString() : '—' }}
              </dd>
            </div>
            <div>
              <dt class="text-muted text-xs uppercase tracking-wide">
                Total
              </dt>
              <dd class="text-highlighted font-medium tabular-nums">
                {{ formatGrosz(invoice.total_amount) }}
              </dd>
            </div>
          </dl>
        </div>

        <div class="ring-default flex flex-col gap-4 rounded-lg p-5 ring">
          <h2 class="text-highlighted font-semibold">
            Status
          </h2>

          <div v-if="transitionItems.length > 0" class="flex flex-wrap items-center gap-2">
            <UButton
              v-for="item in transitionItems"
              :key="item.value"
              :label="`Mark as ${item.label}`"
              :color="invoiceStatusColor(item.value)"
              variant="subtle"
              :loading="form.processing"
              @click="submitStatus(item.value)"
            />
          </div>
          <p v-else class="text-muted text-sm">
            This invoice is {{ invoice.status }} and has no further status changes available.
          </p>
        </div>

        <div class="ring-default flex flex-col gap-4 rounded-lg p-5 ring">
          <h2 class="text-highlighted font-semibold">
            Notes
          </h2>

          <UFormField name="notes" :error="firstFormError(form.errors.notes)">
            <UTextarea
              v-model="form.notes"
              :rows="4"
              :disabled="invoice.closed"
              placeholder="Internal notes about this invoice."
              class="w-full"
            />
          </UFormField>

          <div v-if="!invoice.closed" class="flex items-center gap-3">
            <UButton
              label="Save notes"
              icon="i-lucide-save"
              color="neutral"
              variant="subtle"
              :loading="form.processing"
              @click="submitNotes"
            />
          </div>
        </div>

        <div class="flex flex-col gap-4">
          <h2 class="text-highlighted font-semibold">
            Line items
          </h2>

          <UTable
            :data="lineItems"
            :columns="columns"
            class="ring-default rounded-lg ring"
          >
            <template #usage_month-cell="{ row }">
              <span class="text-muted text-sm">{{ settlementMonth(row.original.usage_month) }}</span>
            </template>

            <template #unit_price-cell="{ row }">
              <span class="tabular-nums">{{ formatGrosz(row.original.unit_price) }}</span>
            </template>

            <template #amount-cell="{ row }">
              <span class="tabular-nums">{{ formatGrosz(row.original.amount) }}</span>
            </template>

            <template #flags-cell="{ row }">
              <div class="flex gap-1">
                <UBadge v-if="row.original.prior_period" color="warning" variant="subtle" size="sm">
                  Prior period
                </UBadge>
                <UBadge v-if="row.original.unpriced" color="error" variant="subtle" size="sm">
                  Unpriced
                </UBadge>
              </div>
            </template>

            <template #empty>
              <div class="text-muted py-6 text-center text-sm">
                No line items on this invoice.
              </div>
            </template>
          </UTable>

          <div class="flex justify-end">
            <p class="text-highlighted font-semibold tabular-nums">
              Total: {{ formatGrosz(invoice.total_amount) }}
            </p>
          </div>
        </div>

        <div class="ring-error/40 flex flex-col gap-4 rounded-lg p-5 ring">
          <div>
            <h2 class="text-highlighted font-semibold">
              Remove invoice
            </h2>
            <p class="text-muted text-sm">
              Permanently delete this invoice and its line items. A future invoice run for the same month may recreate it.
            </p>
          </div>
          <div>
            <UButton
              label="Remove invoice"
              icon="i-lucide-trash-2"
              color="error"
              variant="subtle"
              @click="deleteOpen = true"
            />
          </div>
        </div>
      </div>
    </template>
  </UDashboardPanel>

  <UModal
    v-model:open="deleteOpen"
    title="Remove invoice"
    description="Delete this invoice and all of its line items? This cannot be undone."
  >
    <template #footer>
      <div class="flex justify-end gap-3">
        <UButton
          label="Cancel"
          color="neutral"
          variant="ghost"
          @click="deleteOpen = false"
        />
        <UButton
          label="Remove"
          color="error"
          icon="i-lucide-trash-2"
          :loading="deleting"
          @click="destroy"
        />
      </div>
    </template>
  </UModal>
</template>
